import { createSelector } from 'reselect';

import {
    selectSearchResults,
    selectTripMetadata,
} from './trips';
import { MULTIMODAL_TYPE } from '../../../constants';
import { selectTravelerInfo } from '../../tripSummary/selectors';

function isNearbyTrip(tripMetadata, tripId) {
    const {
        exactOriginMatch,
        exactDestMatch,
    } = tripMetadata[tripId];

    return !exactOriginMatch || !exactDestMatch;
}

// Mirrors the M1 pax-aware logic in wanderu-ducks-trips/src/appstate/selectors.js
// (selectTripPrices): when trip.seats matches the requested seats, trip.pricing
// is already the N-pax total. Otherwise it's per-pax and must be multiplied so
// the sidebar's "from $X" matches the card's multi-pax total. (#983)
//
// trip.pricing can be either an array of currency objects ([{ USD: 100 }] — the
// shape i18nMoney().getTotalPrice() reduces over in production) or a single
// currency object ({ USD: 100 } — the shape some test fixtures use). Preserve
// whichever shape was passed in so downstream consumers don't crash.
function multiplyPriceObj(priceObj, seats) {
    return Object.entries(priceObj).reduce((acc, [currency, price]) => ({
        ...acc,
        [currency]: price * seats,
    }), {});
}

function paxAwarePricing(pricing, tripSeats, seats) {
    if (!pricing || seats <= 1) {
        return pricing;
    }
    if (tripSeats === seats) {
        return pricing;
    }
    if (Array.isArray(pricing)) {
        return pricing.map((priceObj) => multiplyPriceObj(priceObj, seats));
    }
    return multiplyPriceObj(pricing, seats);
}

function minPriceByCarrier(allowNearbyIsFiltered, trips = [], tripMetadata, seats = 1) {
    let minPriceObj = {};
    trips.forEach((trip) => {
        const tripLegs = trip.isRouted
            ? trip.tripLegs.filtered
            : [trip];

        const {
            tripModes,
            tripId,
        } = trip;
        const isMultiModal = new Set([...tripModes]).size > 1;
        const tripSeats = Number.parseInt(trip.seats, 10);

        tripLegs.forEach((leg) => {
            const { carrierCode } = leg;
            const tripModes = isMultiModal
                ? [MULTIMODAL_TYPE]
                : leg.tripModes;

            if (!minPriceObj[carrierCode]) {
                minPriceObj[carrierCode] = {};
            }

            if (allowNearbyIsFiltered && isNearbyTrip(tripMetadata, tripId)) {
                return;
            }

            tripModes.forEach((travelMode) => {
                if (!minPriceObj[carrierCode][travelMode]) {
                    minPriceObj[carrierCode][travelMode] = {};
                }

                const minPricing = minPriceObj[carrierCode][travelMode];
                const USDPricing = trip._priceInUSDForSorting;
                if (!minPricing.USDForSort || minPricing.USDForSort > USDPricing) {
                    minPriceObj[carrierCode][travelMode] = {
                        pricing: paxAwarePricing(trip.pricing, tripSeats, seats),
                        USDForSort: USDPricing,
                    };
                }
            });
        });
    });

    return minPriceObj;
}

const selectMinPriceByCarrier = createSelector(
    (state, searchParams, allowNearbyIsFiltered) => allowNearbyIsFiltered,
    selectSearchResults,
    selectTripMetadata,
    (state) => (state ? selectTravelerInfo(state)?.seats || 1 : 1),
    minPriceByCarrier,
);

function groupCarriersByMode(allowNearbyIsFiltered, trips = [], tripMetadata) {
    const carrierByModeMap = {};
    trips.forEach((trip) => {
        let {
            tripId,
            isRouted,
            tripLegs,
            tripModes,
        } = trip;

        tripLegs = isRouted
            ? tripLegs.filtered
            : [trip];

        const isMultiModal = new Set([...tripModes]).size > 1;

        tripLegs.forEach((leg) => {
            let {
                carrierCode,
                tripModes,
            } = leg;

            if (allowNearbyIsFiltered && isNearbyTrip(tripMetadata, tripId)) {
                return;
            }

            if (isMultiModal) {
                tripModes = [MULTIMODAL_TYPE];
            }

            tripModes.forEach((mode) => {
                if (!carrierByModeMap[mode]) {
                    carrierByModeMap[mode] = new Set();
                }

                carrierByModeMap[mode].add(carrierCode);
            });
        });
    });

    return Object.entries(carrierByModeMap)
        .reduce((map, [travelMode, set]) => ({
            ...map,
            [travelMode]: [...set],
        }), {});
}

const selectCarriersByMode = createSelector(
    (state, searchParams, allowNearbyIsFiltered) => allowNearbyIsFiltered,
    selectSearchResults,
    selectTripMetadata,
    groupCarriersByMode,
);

function groupModesByCarriers(carriersByModeMap) {
    return Object.entries(carriersByModeMap).reduce((map, [travelMode, carriers]) => {
        return carriers.reduce((map, carrierCode) => {
            if (map[carrierCode] && map[carrierCode].includes(travelMode)) {
                return map;
            }

            return {
                ...map,
                [carrierCode]: [
                    ...map[carrierCode] || [],
                    travelMode,
                ],
            };
        }, map);
    }, {});
}

const selectCarrierModesMap = createSelector(
    selectCarriersByMode,
    groupModesByCarriers,
);

const selectCarriersTripData = createSelector(
    selectMinPriceByCarrier,
    (minByCarrier) => {
        return Object.entries(minByCarrier).reduce((map, [carrierCode, travelModeObj]) => {
            return Object.entries(travelModeObj).reduce((map, [travelMode, pricingObj]) => {
                return {
                    ...map,
                    [carrierCode]: {
                        ...map[carrierCode] || {},
                        [travelMode]: {
                            minPricing: pricingObj.pricing,
                        },
                    },
                };
            }, map);
        }, {});
    },
);

export {
    selectCarrierModesMap,
    selectCarriersByMode,
    selectCarriersTripData,
    selectMinPriceByCarrier,
};
